import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Sparkles, Upload, Loader2, Search, Play, X, FileText, AlertCircle, CheckCircle,
  Database, Building2, UserSquare2, Calendar, Settings, AlertTriangle, ListChecks,
  Brain, RefreshCw, Square, CheckSquare,
} from 'lucide-react';

async function req(url, opts = {}, raw = false) {
  const token = localStorage.getItem('token');
  const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}${url}`, {
    ...opts,
    headers: { Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
  });
  if (raw) return r;
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
  return data;
}

export default function EnriquecimientoCorreo() {
  const navigate = useNavigate();
  const [cuentas, setCuentas] = useState([]);
  const [filtroCuenta, setFiltroCuenta] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [usarPerplexity, setUsarPerplexity] = useState(false);

  const [estimacion, setEstimacion] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [seleccionadas, setSeleccionadas] = useState(new Set());

  const [uploadId, setUploadId] = useState(null);
  const [uploadInfo, setUploadInfo] = useState(null);
  const fileRef = useRef(null);

  const [job, setJob] = useState(null);
  const [resultado, setResultado] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    req('/api/correo/cuentas').then((c) => setCuentas(Array.isArray(c) ? c : [])).catch(() => setCuentas([]));
  }, []);

  // Polling del job
  useEffect(() => {
    if (!job || !job.id) return undefined;
    if (['completado', 'cancelado', 'error'].includes(job.estado)) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const j = await req(`/api/correo/enriquecimiento/jobs/${job.id}`);
        setJob(j);
        if (['completado', 'cancelado', 'error'].includes(j.estado)) {
          clearInterval(pollRef.current);
        }
      } catch (e) { console.error(e); }
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [job?.id, job?.estado]);

  const buildBody = () => ({
    cuenta_id: filtroCuenta || null,
    fecha_desde: fechaDesde || null,
    fecha_hasta: fechaHasta || null,
    upload_id: uploadId || null,
  });

  const estimar = async () => {
    try {
      const r = await req('/api/correo/enriquecimiento/estimar', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildBody()),
      });
      setEstimacion(r);
    } catch (e) { toast.error(`Estimar: ${e.message}`); }
  };

  const cargarPreview = async () => {
    setPreviewLoading(true);
    setPreview(null);
    setSeleccionadas(new Set());
    try {
      const r = await req('/api/correo/enriquecimiento/preview', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildBody()),
      });
      setPreview(r);
      const set = new Set();
      (r.items || []).forEach((it) => {
        if (it.accion === 'crear_ambos' || it.accion === 'crear_contacto' || it.accion === 'crear_empresa_solo') {
          set.add(it.message_id);
        }
      });
      setSeleccionadas(set);
    } catch (e) { toast.error(`Preview: ${e.message}`); }
    finally { setPreviewLoading(false); }
  };

  const procesarSeleccionados = async () => {
    if (!preview || seleccionadas.size === 0) return;
    const items = preview.items.filter((it) => seleccionadas.has(it.message_id));
    try {
      const r = await req('/api/correo/enriquecimiento/procesar', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, usar_perplexity: usarPerplexity }),
      });
      setResultado(r);
      toast.success(`Procesados ${r.procesados}: ${r.empresas_creadas} empresas + ${r.contactos_creados} contactos creados`);
      cargarPreview();
    } catch (e) { toast.error(`Procesar: ${e.message}`); }
  };

  const lanzarMasivo = async () => {
    if (!window.confirm(`Vas a procesar todos los correos del filtro actual con Gemini${usarPerplexity ? ' + Perplexity' : ''}. Esto puede tardar varios minutos. ¿Confirmas?`)) return;
    try {
      const r = await req('/api/correo/enriquecimiento/masivo', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...buildBody(), usar_perplexity: usarPerplexity }),
      });
      toast.success(`Job lanzado (${r.total} correos)`);
      const j = await req(`/api/correo/enriquecimiento/jobs/${r.job_id}`);
      setJob(j);
      setResultado(null);
    } catch (e) { toast.error(`Masivo: ${e.message}`); }
  };

  const cancelarJob = async () => {
    if (!job) return;
    try {
      await req(`/api/correo/enriquecimiento/jobs/${job.id}/cancelar`, { method: 'POST' });
      toast.message('Cancelación solicitada — el worker parará en el siguiente checkpoint');
    } catch (e) { toast.error(e.message); }
  };

  const subirArchivo = async (file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Archivo > 50 MB. Divide tu mbox por años.');
      return;
    }
    const fd = new FormData();
    fd.append('archivo', file);
    try {
      const r = await req('/api/correo/enriquecimiento/subir-archivo', { method: 'POST', body: fd });
      setUploadId(r.upload_id);
      setUploadInfo(r);
      toast.success(`${r.correos_parseados} correos parseados de ${r.archivo}`);
      // Limpiar filtros de cuenta/fecha para que el upload tenga prioridad
      setFiltroCuenta('');
      setFechaDesde('');
      setFechaHasta('');
    } catch (e) { toast.error(`Subir archivo: ${e.message}`); }
  };

  const forzarReanalisis = async () => {
    if (!window.confirm('Esto borra el flag "enriquecido" de todos tus correos. Volverán a aparecer como pendientes de procesar. ¿Continuar?')) return;
    try {
      const r = await req('/api/correo/enriquecimiento/forzar-reanalisis', { method: 'POST' });
      toast.success(`${r.correos_reseteados} correos disponibles para re-análisis`);
    } catch (e) { toast.error(e.message); }
  };

  const togglesel = (mid) => {
    const s = new Set(seleccionadas);
    if (s.has(mid)) s.delete(mid); else s.add(mid);
    setSeleccionadas(s);
  };
  const toggleAll = () => {
    if (!preview) return;
    if (seleccionadas.size === preview.items.length) setSeleccionadas(new Set());
    else setSeleccionadas(new Set(preview.items.map((it) => it.message_id)));
  };

  const accionLabel = {
    ignorar: { txt: 'Ignorar (sin CIF)', cls: 'bg-ink-100 text-ink-600 border-ink-200' },
    buscar_cif: { txt: 'Buscar CIF', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    vincular_solo: { txt: 'Solo vincular', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
    crear_contacto: { txt: 'Crear contacto', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
    crear_empresa_solo: { txt: 'Crear empresa', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
    crear_ambos: { txt: 'Crear ambos', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-5" data-testid="enriquecimiento-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-ink-900 font-display tracking-tight flex items-center gap-2">
            <Brain className="w-7 h-7 text-violet-500" /> Enriquecimiento CRM desde correos
          </h1>
          <p className="text-sm text-ink-500 mt-1 max-w-3xl">
            Detecta automáticamente empresas y contactos en tus correos (firma → CIF → cruce con CRM). Los registros nuevos quedan
            marcados como <span className="font-medium">pendientes de revisión</span> y se ocultan en los listados normales hasta que un admin los apruebe.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={forzarReanalisis} title="Resetea el flag enriquecido_at de todos tus correos" data-testid="btn-forzar-reanalisis">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Forzar re-análisis
        </Button>
      </div>

      {/* Filtros + upload */}
      <Card className="p-4" data-testid="seccion-filtros">
        <h2 className="text-xs uppercase tracking-wider font-semibold text-ink-500 mb-3 flex items-center gap-1.5">
          <Settings className="w-3.5 h-3.5" /> Origen de los correos
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-ink-500">Cuenta</label>
            <select className="w-full mt-1 border border-ink-200 rounded px-2 py-1.5 text-sm bg-white" value={filtroCuenta} onChange={(e) => setFiltroCuenta(e.target.value)} data-testid="filtro-cuenta-enriq" disabled={!!uploadId}>
              <option value="">Todas las cuentas</option>
              {cuentas.map((c) => <option key={c.id} value={c.id}>{c.email}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-ink-500">Desde</label>
            <Input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} data-testid="filtro-desde" disabled={!!uploadId} />
          </div>
          <div>
            <label className="text-xs text-ink-500">Hasta</label>
            <Input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} data-testid="filtro-hasta" disabled={!!uploadId} />
          </div>
          <div>
            <label className="text-xs text-ink-500">o subir archivo</label>
            <input ref={fileRef} type="file" accept=".eml,.mbox,.zip" onChange={(e) => subirArchivo(e.target.files?.[0])} className="hidden" data-testid="input-file" />
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} className="w-full mt-1" data-testid="btn-subir-archivo">
              <Upload className="w-4 h-4 mr-1.5" /> .eml / .mbox / .zip
            </Button>
          </div>
        </div>
        {uploadInfo && (
          <div className="mt-3 p-2 bg-violet-50 border border-violet-200 rounded text-xs flex items-center justify-between" data-testid="upload-info">
            <span className="text-violet-800">
              📎 <strong>{uploadInfo.archivo}</strong> · {uploadInfo.correos_parseados} correos parseados (TTL {Math.round((uploadInfo.ttl_segundos || 0) / 60)} min)
            </span>
            <Button size="sm" variant="ghost" onClick={() => { setUploadId(null); setUploadInfo(null); }}>
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
        <div className="mt-3 flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={usarPerplexity} onChange={(e) => setUsarPerplexity(e.target.checked)} data-testid="toggle-perplexity" />
            <span>Enriquecer con búsqueda web (Perplexity) para empresas sin CIF en firma</span>
            <Badge className="bg-amber-50 text-amber-700 border-amber-200 border text-[9px]">Coste extra</Badge>
          </label>
        </div>
      </Card>

      {/* Estimar y Lanzar */}
      <Card className="p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={estimar} data-testid="btn-estimar">
              <Database className="w-4 h-4 mr-1.5" /> Estimar coste
            </Button>
            <Button size="sm" variant="outline" onClick={cargarPreview} disabled={previewLoading} data-testid="btn-preview">
              {previewLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Search className="w-4 h-4 mr-1.5" />}
              Analizar (preview)
            </Button>
          </div>
          <Button size="sm" onClick={lanzarMasivo} disabled={!!job && !['completado', 'cancelado', 'error'].includes(job.estado)} className="bg-violet-600 hover:bg-violet-700" data-testid="btn-lanzar-masivo">
            <Play className="w-4 h-4 mr-1.5" /> Procesar todo (masivo)
          </Button>
        </div>
        {estimacion && (
          <div className="mt-3 p-3 bg-violet-50/50 border border-violet-200 rounded text-xs grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="estimacion-resultado">
            <div>
              <div className="text-ink-500">Correos</div>
              <div className="text-lg font-bold text-violet-700">{estimacion.total_correos}</div>
            </div>
            <div>
              <div className="text-ink-500">Llamadas Gemini</div>
              <div className="text-lg font-bold text-violet-700">{estimacion.llamadas_gemini_estimadas}</div>
            </div>
            <div>
              <div className="text-ink-500">Coste aprox.</div>
              <div className="text-lg font-bold text-violet-700">${estimacion.coste_gemini_usd_aprox}</div>
            </div>
            <div>
              <div className="text-ink-500">Duración estimada</div>
              <div className="text-lg font-bold text-violet-700">{estimacion.duracion_estimada_min} min</div>
            </div>
            <div className="col-span-full text-ink-500 text-[11px] mt-1">{estimacion.advertencia_perplexity}</div>
          </div>
        )}
      </Card>

      {/* Job en curso */}
      {job && (
        <Card className="p-4 border-violet-300 bg-violet-50/40" data-testid="job-actual">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Loader2 className={`w-4 h-4 ${['en_curso', 'pendiente'].includes(job.estado) ? 'animate-spin' : ''} text-violet-600`} />
              Job: {job.estado}
            </h2>
            {['pendiente', 'en_curso'].includes(job.estado) && (
              <Button size="sm" variant="outline" onClick={cancelarJob} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="btn-cancelar-job">
                <X className="w-3.5 h-3.5 mr-1" /> Cancelar
              </Button>
            )}
          </div>
          <div className="w-full bg-ink-200 rounded h-3 overflow-hidden">
            <div
              className="bg-violet-500 h-full transition-all"
              style={{ width: `${Math.min(100, Math.round((job.procesados / Math.max(1, job.total)) * 100))}%` }}
              data-testid="job-progress-bar"
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3 text-xs">
            <Stat label="Procesados" v={`${job.procesados} / ${job.total}`} />
            <Stat label="Empresas creadas" v={job.empresas_creadas} color="text-emerald-700" />
            <Stat label="Empresas vinculadas" v={job.empresas_vinculadas} color="text-sky-700" />
            <Stat label="Contactos creados" v={job.contactos_creados} color="text-emerald-700" />
            <Stat label="Ignorados" v={job.ignorados} color="text-ink-500" />
          </div>
        </Card>
      )}

      {/* Preview tabla */}
      {preview && (
        <Card className="p-0 overflow-hidden" data-testid="preview-tabla">
          <div className="p-3 border-b border-ink-200 flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm">
              <span className="font-semibold">{preview.extraidos}</span> con firma de {preview.total} correos · <span className="text-ink-500">{preview.sin_firma} sin firma · {preview.sin_cif} sin CIF</span>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={toggleAll} data-testid="btn-toggle-all">
                {seleccionadas.size === preview.items.length ? 'Deseleccionar todo' : 'Seleccionar todo'}
              </Button>
              <Button size="sm" onClick={procesarSeleccionados} disabled={seleccionadas.size === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-procesar-seleccionados">
                <Play className="w-3.5 h-3.5 mr-1" /> Procesar {seleccionadas.size} seleccionado(s)
              </Button>
            </div>
          </div>
          {preview.items.length === 0 ? (
            <div className="p-8 text-center text-sm text-ink-500" data-testid="preview-vacio">
              No se ha detectado información de empresa/contacto en estos correos. Prueba a sincronizar más histórico desde Mi perfil → ⚙ Sync, o sube un archivo .mbox.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-ink-50 text-ink-600 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="p-2 text-left w-8"></th>
                    <th className="p-2 text-left">Email</th>
                    <th className="p-2 text-left">Empresa</th>
                    <th className="p-2 text-left">CIF</th>
                    <th className="p-2 text-left">CRM</th>
                    <th className="p-2 text-left">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.items.map((it) => (
                    <tr key={it.message_id} className="border-t border-ink-100 hover:bg-ink-50/40" data-testid={`preview-row-${it.message_id}`}>
                      <td className="p-2 align-top">
                        <button onClick={() => togglesel(it.message_id)} data-testid={`check-${it.message_id}`}>
                          {seleccionadas.has(it.message_id) ? <CheckSquare className="w-4 h-4 text-violet-600" /> : <Square className="w-4 h-4 text-ink-400" />}
                        </button>
                      </td>
                      <td className="p-2 align-top">
                        <div className="font-medium text-ink-800 truncate max-w-[180px]">{it.from_address}</div>
                        <div className="text-[10px] text-ink-400">{it.contacto_nombre} {it.contacto_apellidos}</div>
                      </td>
                      <td className="p-2 align-top">
                        <div className="text-ink-800 truncate max-w-[200px]">{it.empresa_detectada || '—'}</div>
                        {it.web_empresa && <div className="text-[10px] text-ink-400 truncate max-w-[200px]">{it.web_empresa}</div>}
                      </td>
                      <td className="p-2 align-top">
                        {it.cif_detectado ? (
                          <span className={`font-mono ${it.cif_valido ? 'text-emerald-700' : 'text-rose-600 line-through'}`}>{it.cif_detectado}</span>
                        ) : <span className="text-ink-400">—</span>}
                      </td>
                      <td className="p-2 align-top">
                        <div>{it.empresa_existe ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border text-[9px]">✓ {it.empresa_existente?.nombre || 'empresa'}</Badge> : <span className="text-ink-400">empresa nueva</span>}</div>
                        <div className="mt-0.5">{it.contacto_existe ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border text-[9px]">✓ contacto</Badge> : <span className="text-ink-400">contacto nuevo</span>}</div>
                      </td>
                      <td className="p-2 align-top">
                        <Badge className={`text-[9px] border ${accionLabel[it.accion]?.cls || ''}`}>{accionLabel[it.accion]?.txt || it.accion}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Resultado modo selectivo */}
      {resultado && !job && (
        <Card className="p-4 border-emerald-300 bg-emerald-50/40" data-testid="resultado-procesado">
          <h2 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-600" /> Procesamiento completado
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
            <Stat label="Procesados" v={resultado.procesados} />
            <Stat label="Empresas creadas" v={resultado.empresas_creadas} color="text-emerald-700" />
            <Stat label="Empresas vinculadas" v={resultado.empresas_vinculadas} color="text-sky-700" />
            <Stat label="Contactos creados" v={resultado.contactos_creados} color="text-emerald-700" />
            <Stat label="Ignorados" v={resultado.ignorados} color="text-ink-500" />
          </div>
          <div className="mt-3 text-xs text-emerald-800 flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5" />
            Los registros creados están marcados como <strong>pendiente de revisión</strong>.
            <button onClick={() => navigate('/clientes?pendiente_revision=true')} className="underline ml-1 hover:text-emerald-900" data-testid="link-revisar">Revisarlos →</button>
          </div>
        </Card>
      )}
    </div>
  );
}

const Stat = ({ label, v, color = 'text-ink-800' }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-ink-500">{label}</div>
    <div className={`text-base font-bold ${color}`}>{v}</div>
  </div>
);
